import { ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';
import { Box, styled, Tooltip } from '@mui/material';
import { countFittingChips } from './countFittingChips';
import { StyledChip } from './details/SharedStyles';

export interface FilterChip {
  key: string;
  chip: ReactNode;
}

const CHIP_GAP = 6;
const OVERFLOW_CHIP_WIDTH = 40;

const ChipRow = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: `${CHIP_GAP}px`,
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  padding: '4px 0',
});

const ChipSlot = styled(Box)({
  display: 'flex',
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

const hiddenSlotStyle = {
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none',
} as const;

const OverflowChip = styled(StyledChip)({
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
});

const OverflowList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  padding: '4px 0',
});

const useVisibleChipCount = (rowReference: RefObject<HTMLDivElement | null>, chipCount: number) => {
  const [visibleCount, setVisibleCount] = useState(chipCount);

  useLayoutEffect(() => {
    const row = rowReference.current;
    if (!row) return;
    const measure = () => {
      const slots = [...row.querySelectorAll<HTMLElement>('[data-chip-slot]')];
      setVisibleCount(
        countFittingChips({
          availableWidth: row.clientWidth,
          chipWidths: slots.map(slot => slot.offsetWidth),
          gap: CHIP_GAP,
          overflowChipWidth: OVERFLOW_CHIP_WIDTH,
        })
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [rowReference, chipCount]);

  return visibleCount;
};

export const CollapsingChipRow = ({ chips }: { chips: FilterChip[] }) => {
  const rowReference = useRef<HTMLDivElement>(null);
  const visibleCount = useVisibleChipCount(rowReference, chips.length);
  const hiddenCount = chips.length - visibleCount;

  return (
    <ChipRow ref={rowReference}>
      {chips.map(({ key, chip }, index) => {
        const isHidden = index >= visibleCount;
        return (
          <ChipSlot key={key} data-chip-slot aria-hidden={isHidden} sx={isHidden ? hiddenSlotStyle : undefined}>
            {chip}
          </ChipSlot>
        );
      })}
      {hiddenCount > 0 && (
        <Tooltip
          title={
            <OverflowList data-testid='filter-overflow-list'>
              {chips.map(({ key, chip }) => (
                <Box key={key}>{chip}</Box>
              ))}
            </OverflowList>
          }
        >
          <OverflowChip label={`+${hiddenCount}`} size='small' color='default' data-testid='filter-overflow-chip' />
        </Tooltip>
      )}
    </ChipRow>
  );
};
